<template>
  <el-select
    v-model="modelVal"
    clearable
    filterable
    :remote="isRemote"
    :remote-method="isRemote ? debouncedBuscar : undefined"
    :filter-method="!isRemote ? filtrarLocal : undefined"
    :loading="carregando"
    no-match-text="Nenhum resultado encontrado"
    no-data-text="Nenhum dado disponível"
    placeholder="Selecionar..."
    style="width: 100%"
    @visible-change="onAbrirDropdown"
    @clear="onLimpar"
  >
    <el-option
      v-for="item in opcoesFiltradas"
      :key="item._id"
      :label="String(item._id)"
      :value="item._id"
    >
      <span>{{ item._id }} - {{ item._name }}</span>
    </el-option>

    <div v-if="mostraRodape" class="ss-rodape" @click.stop>
      <span class="ss-contagem">{{ rotuloContagem }}</span>
      <span v-if="temMais" class="ss-mais" @click="carregarMais">
        Carregar mais registros
      </span>
    </div>
  </el-select>
</template>

<script>
import ncformCommon from '@ncform/ncform-common';
import { get } from '@/helpers/apiRequest';

export default {
  name: 'SearchSelectWidget',
  mixins: [ncformCommon.mixins.vue.controlMixin],

  data() {
    return {
      opcoes: [],
      carregando: false,
      filtro: '',
      timer: null,
      total: 0,
      pagina: 1,
      // Último termo consultado no servidor. Guardado porque o Element UI zera o
      // texto digitado ao fechar o dropdown sem avisar o `remote-method`.
      termo: '',
      // Sequência da requisição: uma resposta atrasada não pode sobrescrever a
      // lista montada por uma busca mais recente.
      requisicao: 0,
    };
  },

  computed: {
    // Busca no servidor é o padrão: só fica no modo de carga total quem pedir
    // `preload: true` de propósito. Assim um campo novo não nasce limitado à
    // primeira página por esquecimento, que foi a origem do chamado.
    isRemote() {
      return this.mergeConfig.preload !== true;
    },
    // Filtros fixos do campo (ex.: { ativo: 1 }), somados à busca. Servem para o
    // campo de seleção não oferecer registro que o vínculo não pode receber.
    extraQuery() {
      const extra = this.mergeConfig.extraParams;
      if (!extra) return '';
      return Object.entries(extra)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => `&${k}=${encodeURIComponent(v)}`)
        .join('');
    },
    opcoesFiltradas() {
      if (!this.filtro || this.isRemote) return this.opcoes;
      const q = this.filtro.toLowerCase();
      return this.opcoes.filter(i =>
        `${i._id} - ${i._name}`.toLowerCase().includes(q)
      );
    },
    temMais() {
      return this.opcoes.length < this.total;
    },
    // Sem o rodapé o usuário vê a primeira página e conclui que aquilo é a lista
    // inteira — foi o que originou o chamado. A contagem fica visível até o fim:
    // é ela que confirma que a lista acabou quando a ação de carregar mais some.
    mostraRodape() {
      return this.isRemote && this.opcoes.length > 0;
    },
    rotuloContagem() {
      const alvo = this.termo ? 'encontrado(s)' : 'registro(s)';
      return `exibindo ${this.opcoes.length} de ${this.total} ${alvo}`;
    },
  },

  methods: {
    filtrarLocal(q) {
      this.filtro = q;
    },
    debouncedBuscar(q) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.buscar(q), 300);
    },
    onAbrirDropdown(visible) {
      if (!visible) return;

      if (!this.isRemote) {
        this.filtro = '';
        return;
      }

      // Ao reabrir, o Element UI limpa o texto digitado e chama o `remote-method`
      // com string vazia — mas engole essa chamada, porque zerou o `previousQuery`
      // ao fechar. Sem refazer a busca aqui, o dropdown reabre em branco mostrando
      // o resultado do termo anterior.
      if (this.opcoes.length === 0 || this.termo !== '') {
        this.buscar('');
      }
    },
    onLimpar() {
      // O × fecha o select sem passar pelo `remote-method`, então a lista também
      // ficaria presa no último termo.
      if (this.isRemote) this.buscar('');
    },
    buscar(query) {
      return this._fetchOpcoes(query, 1);
    },
    carregarMais() {
      return this._fetchOpcoes(this.termo, this.pagina + 1);
    },
    async _fetchOpcoes(query, pagina = 1) {
      const cfg = this.mergeConfig;
      if (!cfg.searchUrl) return;
      const idField = cfg.idField || 'id';
      const labelField = cfg.labelField || 'descricao';
      const labelFields = cfg.labelFields;
      // No modo remoto quem decide o tamanho da página é a API; o parâmetro serve
      // só para ligar a paginação. No modo preload o campo depende de receber a
      // lista inteira de uma vez — e a API pagina no tamanho dela de qualquer
      // forma, então esse modo enxerga apenas a primeira página: use-o só em
      // lista curta, que não cresce com o cadastro do cliente.
      const paginacao = this.isRemote ? `page=${pagina}` : 'take=500';
      const requisicao = ++this.requisicao;
      this.carregando = true;
      try {
        const qs = query ? `&q=${encodeURIComponent(query)}` : '';
        const data = await get(`${cfg.searchUrl}?${paginacao}${qs}${this.extraQuery}`);
        if (requisicao !== this.requisicao) return;
        const itens = data.itens || data.data || (Array.isArray(data) ? data : []);
        const novas = itens.map(item => ({
          ...item,
          _id: item[idField],
          _name: labelFields && labelFields.length
            ? labelFields.map(f => item[f]).filter(Boolean).join(' / ')
            : item[labelField] || '',
        }));
        this.opcoes = pagina > 1 ? this.opcoes.concat(novas) : novas;
        this.total = (data.paginator && data.paginator.total) || this.opcoes.length;
        this.pagina = pagina;
        this.termo = query || '';
      } catch {
        if (requisicao !== this.requisicao) return;
        this.opcoes = [];
        this.total = 0;
      } finally {
        if (requisicao === this.requisicao) this.carregando = false;
      }
    },
  },

  mounted() {
    if (!this.isRemote && this.mergeConfig.searchUrl) {
      this._fetchOpcoes('');
    }
  },
};
</script>

<style scoped>
.ss-rodape {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 20px;
  border-top: 1px solid #f0f2f5;
  background: #fbfcfd;
  cursor: default;
}

.ss-contagem {
  font-size: 11px;
  color: #98a2b3;
}

.ss-mais {
  font-size: 12px;
  font-weight: 600;
  color: #3d7fc4;
  cursor: pointer;
}

.ss-mais:hover {
  text-decoration: underline;
}
</style>
